// Import Floating UI for use in "floating" elements like tooltips, popovers, dropdowns, etc.
import {
  computePosition,
  autoPlacement,
  shift,
  offset,
  autoUpdate,
  arrow,
} from "@floating-ui/dom";

// Import Hook modules
import alerts from "./alerts";
import calendar from "./calendar";
import colorpicker from "./profile_field_types/colorpicker";
import cropper from "./cropper";
import devtools from "./devtools";
import formFields from "./form_fields";
import groupActivityTracker from "./group_activity_tracker";
import groupDues from "./group_dues";
import groupEmailer from "./group_emailer";
import groupRecruitment from "./group_recruitment";
import groupStatusUpdater from "./group_status_updater";
import groupWebsiteEditorPageReorder from "./group_website_editor/page_reorder";
import groupWebsiteEditorWysiwygEditor from "./group_website_editor/wysiwyg_editor";
import imagepicker from "./profile_field_types/imagepicker";
import publicPayments from "./public_payments";
import tagsEditor from "./profile_field_types/tags_editor";
import telephone from "./profile_field_types/telephone";
import websiteTemplateDesignerHtmlEditor from "./website_template_designer/html_editor";
import sparkline from "./sparkline";

function serialize(form) {
  const data = new FormData(form);
  const object = {};

  data.entries().forEach(([key, value]) => {
    // Split nested input names
    const [inputGroup, fieldName] = key
      .split("[")
      .map((str) => str.replace("]", ""));

    if (fieldName === undefined) {
      // Wasn't a nested input name
      object[key] = value;
    } else if (object[inputGroup] === undefined) {
      // create an object for this inputGroup
      object[inputGroup] = { [fieldName]: [value] };
    } else if (Array.isArray(object[inputGroup][fieldName])) {
      // handle if field is an array of values
      object[inputGroup][fieldName].push(value);
    } else {
      object[inputGroup][fieldName] = value;
    }
  });
  return object;
}

const Hooks = {};

/* TODO: Many of the hooks below should be moved into their own files */
Hooks.CountdownTimer = {
  mounted() {
    const { duration } = this.el.dataset;

    if (!duration) {
      return;
    }

    const endTime = Date.now() + parseInt(duration, 10);

    const updateTimer = () => {
      const remainingTime = Math.max(0, endTime - Date.now());
      const minutes = Math.floor(remainingTime / 60000);
      const seconds = Math.floor((remainingTime % 60000) / 1000);

      this.el.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`;
      if (remainingTime > 0) {
        setTimeout(updateTimer, 1000);
      }
    };

    updateTimer();
  },
};

/* LOOKUP / CONTACT LOOKUP */

Hooks.LookupInput = {
  mounted() {
    this.el.addEventListener("keyup", (event) => {
      if (event.code === "Tab") {
        // Don't trigger suggest/search event for a tab key press.
        return;
      }

      clearTimeout(this.loadMoreTimer);

      this.loadMoreTimer = setTimeout(() => {
        this.pushEventTo(this.el, "suggest", { value: this.el.value });
      }, 500);
    });
  },
};

// Add infinite scroll behavior to Lookup results
// Used by Lookup fields and the ContactLookup component/Impersonate feature
Hooks.LookupResults = {
  mounted() {
    this.el.addEventListener("scroll", (e) => {
      clearTimeout(this.loadMoreTimer);

      this.loadMoreTimer = setTimeout(() => {
        const resultsList = e.target;
        const hasMoreResults = "hasMoreResults" in this.el.dataset;
        const loadingMoreResults = "loadingMoreResults" in this.el.dataset;
        const userScrolledNearBottom =
          resultsList.scrollTop + resultsList.clientHeight + 150 >
          resultsList.scrollHeight;

        if (hasMoreResults && !loadingMoreResults && userScrolledNearBottom) {
          this.pushEventTo(this.el, "load-more-results");
        }
      }, 250);
    });
  },
};

Hooks.MultiLookupInput = {
  mounted() {
    this.el.addEventListener("keyup", () => {
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        this.pushEventTo(this.el, "filter", { value: this.el.value });
      }, 500);
    });
  },
};

Hooks.MultiLookupResults = {
  mounted() {
    this.el.addEventListener("scroll", (e) => {
      clearTimeout(this.loadMoreTimer);

      this.loadMoreTimer = setTimeout(() => {
        const resultsList = e.target;
        const userScrolledNearBottom =
          resultsList.scrollTop + resultsList.clientHeight + 150 >
          resultsList.scrollHeight;

        if (userScrolledNearBottom) {
          this.pushEventTo(this.el, "load-more-results");
        }
      }, 250);
    });
  },
};

// Place focus on search input on page load
Hooks.ContactLookupInput = {
  mounted() {
    if (this.el.classList.contains("is-focused")) {
      this.el.focus();
    }
  },
};

Hooks.flash = {
  mounted() {
    const type =
      this.el.dataset.type === "error" ? "danger" : this.el.dataset.type;

    this.pushEvent("lv:clear-flash", { key: type });

    window.toast({
      message: this.el.textContent,
      type: `is-${type}`,
    });
  },
};

/* DROPDOWN MENUS */
Hooks.DropdownTrigger = {
  mounted() {
    this.el.addEventListener("click", window.handleDropdownClick);
  },
  destroyed() {
    this.el.removeEventListener("click", window.handleDropdownClick);
  },
};

/* CONTACT PROFILE */

Hooks.ToggleEditMode = {
  mounted() {
    // When clicked, show loading spinner
    this.el.addEventListener("click", () => {
      const { editable, editFieldClicked } = this.el.dataset;

      // Give immediate feedback to user while we wait for a server response
      this.el.classList.toggle("is-loading");

      // Send event to server
      this.pushEvent("toggle-edit-mode", {
        editable,
        edit_field_clicked: editFieldClicked || "",
      });
    });
  },
};

Hooks.ButtonLoadingOnClick = {
  mounted() {
    // When clicked, show loading spinner
    this.el.addEventListener("click", () => {
      // Give immediate feedback to user while we wait for a server response

      // For Bulma
      this.el.classList.toggle("is-loading");
      // For Tailwind
      this.el.classList.toggle("loading");
    });
  },
};

/* ROSTER */

Hooks.ContactCard = {
  mounted() {
    // If Card is not selectable, ignore this call
    if (!this.el.classList.contains("is-selectable")) {
      return;
    }

    // When a Contact Card is clicked/selected, highlight it
    this.el.addEventListener("click", (e) => {
      // If options menu dropdown is clicked, don't do anything
      if (e.target.closest(".dropdown")) {
        return;
      }

      // Give immediate feedback to user while we wait for a server response
      const card = this.el.closest(".card");
      card.classList.toggle("is-selected");
      card.querySelector(".icon.is-selectable").classList.toggle("is-selected");

      const { contactId } = this.el.dataset;

      // Send event to server
      this.pushEvent("select-toggle", { contact_id: contactId });
    });
  },
};

Hooks.GroupSelect = {
  mounted() {
    const { hcpgId } =
      document.getElementsByTagName("meta").current_user.dataset;
    const recentGroupsStr =
      localStorage.getItem(`recentGroups-${hcpgId}`) || "[]";
    const recentGroups = JSON.parse(recentGroupsStr);
    this.pushEvent("set_recent_groups", {
      recent_groups: recentGroups.reverse(),
    });
  },
  reconnected() {
    const { hcpgId } =
      document.getElementsByTagName("meta").current_user.dataset;
    const recentGroupsStr =
      localStorage.getItem(`recentGroups-${hcpgId}`) || "[]";
    const recentGroups = JSON.parse(recentGroupsStr);
    this.pushEvent("set_recent_groups", {
      recent_groups: recentGroups.reverse(),
    });
  },
};

Hooks.ContactCardSelectAll = {
  mounted() {
    // When the "Select All" checkbox is toggled, either highlight all Contact Cards or none
    this.el.addEventListener("click", () => {
      // Give immediate feedback to user while we wait for a server response
      document.querySelectorAll(".card").forEach((card) => {
        card.classList.toggle("is-selected");
        card
          .querySelector(".icon.is-selectable")
          .classList.toggle("is-selected");
      });

      // Send event to server
      this.pushEvent("select-all", { select_all: this.el.checked });
    });
  },
};

/* CREATE/EDIT MODALS */
/* For CROs, Recruitment, Group Roles */
Hooks.OpenCreateModal = {
  mounted() {
    // When clicked, open Create modal on page
    this.el.addEventListener("click", () => {
      // Give immediate feedback to user while we wait for a server response
      this.el.classList.add("is-loading");

      // Send event to server
      this.pushEvent("open-create-modal");
    });
  },
};

Hooks.OpenEditModal = {
  mounted() {
    // When clicked, open Create modal on page
    this.el.addEventListener("click", () => {
      // Give immediate feedback to user while we wait for a server response
      this.el
        .closest(".dropdown")
        .querySelector(".button")
        .classList.add("is-loading");

      const { id } = this.el.dataset;

      // Send event to server
      this.pushEvent("open-edit-modal", { id });
    });
  },
};

Hooks.OpenDeleteModal = {
  mounted() {
    // When clicked, open Create modal on page
    this.el.addEventListener("click", () => {
      // Give immediate feedback to user while we wait for a server response
      this.el
        .closest(".dropdown")
        .querySelector(".button")
        .classList.add("is-loading");

      const { id } = this.el.dataset;

      // Send event to server
      this.pushEvent("open-delete-modal", { id });
    });
  },
};

/* CONTACT RELATED OBJECTS */

const toggleAccountFields = (fields, hideAccountLookup) => {
  const [lookup, text] = fields;

  if (hideAccountLookup) {
    // Show Text field, hide Lookup
    text.classList.remove("is-hidden");
    lookup.disabled = true;
  } else {
    // Show Lookup, hide Text field
    lookup.disabled = false;
    text.classList.add("is-hidden");
  }
};

const doAccountFieldsToggle = (fields, lv) => {
  toggleAccountFields(fields, lv.el.checked);

  // Send event to server
  lv.pushEvent("toggle-account-fields", { hide_account_lookup: lv.el.checked });
};

Hooks.ToggleAccountFields = {
  mounted() {
    // Collect Account Field Lookup and Text input
    const accountFields = this.el
      .closest("fieldset")
      .querySelectorAll(".account-field");

    // If Text input is filled (on object edit),
    // force check `@hide_account_lookup?` checkbox
    // since Lookup is not being used in this case
    if (accountFields[1].querySelector("input").value.length > 0) {
      this.el.checked = true;
    }

    // Run on load
    doAccountFieldsToggle(accountFields, this);

    // When the ToggleAccountFields checkbox is toggled,
    // switch between Account Lookup and text input
    this.el.addEventListener("click", () => {
      doAccountFieldsToggle(accountFields, this);
    });
  },

  updated() {
    const accountFields = this.el
      .closest("fieldset")
      .querySelectorAll(".account-field");

    doAccountFieldsToggle(accountFields, this);
  },
};

const scrollAt = (el) => {
  const { scrollTop, scrollHeight, clientHeight } = el;

  return (scrollTop / (scrollHeight - clientHeight)) * 100;
};

// Thank you Chris McCord <3
// https://elixirforum.com/t/how-can-i-implement-an-infinite-scroll-in-liveview/30457/2?u=barkerja
// Note: This needs to be cleaned up, and can be improved upon.
Hooks.Paginate = {
  cursor() {
    return this.el.dataset.cursor;
  },

  // Ideally, this would be sent as a message directly from the backend,
  // instead of using a data attribute
  loading() {
    return this.el.dataset.loading === "true";
  },

  mounted() {
    this.pending = this.cursor();

    this.el.addEventListener("scroll", (e) => {
      const phxTarget = e.target.getAttribute("phx-target");

      if (!this.cursor() || this.loading() === true) return;

      if (this.pending === this.cursor() && scrollAt(this.el) >= 90) {
        // Give immediate feedback to user while we wait for a server response
        this.el
          .querySelector(".kanban-pagination-loader")
          .classList.remove("is-hidden");

        this.pending = null;
        this.pushEventTo(phxTarget, "load-more-leads", null);
      }
    });
  },

  reconnected() {
    this.pending = this.cursor();
  },
  updated() {
    this.pending = this.cursor();
  },
};

/**
 * "CREATE & CREATE ANOTHER" FORM BUTTON
 * Reusable logic for "Create & Create Another" button in Forms
 *
 * Usage:
 * Add `<button type="button" id="id-required" phx-hook="CreateAnother" data-redirect-to="/path">`
 */

// Wait for hidden input to render in DOM
// and then submit the form after
const waitThenSubmitForm = (input) => {
  if (input) {
    // Since this is a Live View, we can't use `form.submit()` directly
    // We piggyback on the actual `submit` button and programatically click it
    // to trigger form submission
    const submitButton = input
      .closest("form")
      .querySelector("button[type=submit]");

    submitButton.click();

    // Remove Input to prevent another redirect in case
    // the user clicks `Create` next time
    input.remove();
  } else {
    window.requestAnimationFrame(() => {
      waitThenSubmitForm(input);
    });
  }
};

Hooks.CreateAnother = {
  mounted() {
    this.el.addEventListener("click", () => {
      const form = this.el.closest("form");

      // Generate valid Input name using a random Input in the Form
      // We do this since forms can have an `as:` key in which
      // all params for the form will are collected
      let formName = form.querySelectorAll("input")[2].name;
      formName = formName.substring(0, formName.indexOf("["));
      const inputName = `${formName}[redirect_to]`;

      // Create hidden Input that will send `redirect_to` path
      // in the Form params
      const input = document.createElement("input");
      input.setAttribute("type", "hidden");
      input.setAttribute("name", inputName);
      input.setAttribute("value", this.el.dataset.redirectTo || "/");

      // Add hidden Input to Form
      form.appendChild(input);

      // Since appendChild() is asynchronous, we need to wait for it to finish
      // and then submit the form afterwards
      waitThenSubmitForm(input);
    });
  },
};

Hooks.Popover = {
  mounted() {
    const button = this.el.querySelector("[data-trigger]");
    const menu = this.el.querySelector("[data-menu]");
    const arrowEl = this.el.querySelector(".arrow");

    function roundByDPR(value) {
      const dpr = window.devicePixelRatio || 1;
      return Math.round(value * dpr) / dpr;
    }

    function update() {
      // See if arrowEl is present...
      if (arrowEl) {
        const arrowLen = arrowEl.offsetWidth;
        const floatingOffset = Math.sqrt(2 * arrowLen ** 2) / 2;

        computePosition(button, menu, {
          middleware: [
            autoPlacement({
              allowedPlacements: ["top", "bottom", "left", "right"],
            }),
            offset(floatingOffset),
            shift({ padding: 10 }),
            arrow({ element: arrowEl }),
          ],
        }).then(({ x, y, placement, middlewareData }) => {
          Object.assign(menu.style, {
            left: `${x}px`,
            top: `${y}px`,
          });

          const side = placement.split("-")[0];

          const staticSide = {
            top: "bottom",
            right: "left",
            bottom: "top",
            left: "right",
          }[side];

          if (middlewareData.arrow) {
            const border = {
              top: {
                borderTopWidth: "1px",
                borderRightWidth: 0,
                borderBottomWidth: 0,
                borderLeftWidth: "1px",
              },
              right: {
                borderTopWidth: "1px",
                borderRightWidth: "1px",
                borderBottomWidth: 0,
                borderLeftWidth: 0,
              },
              bottom: {
                borderTopWidth: 0,
                borderRightWidth: "1px",
                borderBottomWidth: "1px",
                borderLeftWidth: 0,
              },
              left: {
                borderTopWidth: 0,
                borderRightWidth: 0,
                borderBottomWidth: "1px",
                borderLeftWidth: "1px",
              },
            };

            // This is fine to overwrite.
            const { x, y } = middlewareData.arrow;

            Object.assign(arrowEl.style, {
              left: x != null ? `${x}px` : "",
              top: y != null ? `${y}px` : "",
              right: "",
              bottom: "",
              [staticSide]: `${-arrowLen / 2}px`,
              transform: "rotate(45deg)",
              ...border[staticSide],
            });
          }
        });
      } else {
        computePosition(button, menu, {
          placement: "top",
          middleware: [offset(6), autoPlacement(), shift({ padding: 6 })],
        }).then(({ x, y }) => {
          Object.assign(menu.style, {
            top: "0",
            left: "0",
            transform: `translate(${roundByDPR(x)}px,${roundByDPR(y)}px)`,
          });
        });
      }
    }

    // Execute this to remove the autoupdater ex: cleanup();
    this.cleanup = autoUpdate(button, menu, update);
  },

  destroyed() {
    this.cleanup();
  },
};

Hooks.Tooltip = {
  mounted() {
    const trigger = this.el;
    const tip = this.el.querySelector("[data-tooltip-fui]");
    const arrowEl = this.el.querySelector(".arrow");

    function update() {
      const arrowLen = arrowEl.offsetWidth;
      const floatingOffset = Math.sqrt(2 * arrowLen ** 2) / 2;

      computePosition(trigger, tip, {
        middleware: [
          autoPlacement({ allowedPlacements: ["top", "bottom"] }),
          offset(floatingOffset),
          shift({ padding: 10 }),
          arrow({ element: arrowEl }),
        ],
      }).then(({ x, y, placement, middlewareData }) => {
        Object.assign(tip.style, {
          left: `${x}px`,
          top: `${y}px`,
        });

        const side = placement.split("-")[0];

        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[side];

        if (middlewareData.arrow) {
          const border = {
            top: {
              borderTopWidth: "",
              borderRightWidth: 0,
              borderBottomWidth: 0,
              borderLeftWidth: "",
            },
            right: {
              borderTopWidth: "",
              borderRightWidth: "",
              borderBottomWidth: 0,
              borderLeftWidth: 0,
            },
            bottom: {
              borderTopWidth: 0,
              borderRightWidth: "",
              borderBottomWidth: "",
              borderLeftWidth: 0,
            },
            left: {
              borderTopWidth: 0,
              borderRightWidth: 0,
              borderBottomWidth: "",
              borderLeftWidth: "",
            },
          };

          // This is fine to overwrite.
          const { x, y } = middlewareData.arrow;

          Object.assign(arrowEl.style, {
            left: x != null ? `${x}px` : "",
            top: y != null ? `${y}px` : "",
            right: "",
            bottom: "",
            [staticSide]: `${-arrowLen / 2}px`,
            transform: "rotate(45deg)",
            ...border[staticSide],
          });
        }
      });
    }

    // Execute this to remove the autoupdater ex: cleanup();
    this.cleanup = autoUpdate(trigger, tip, update);
  },

  destroyed() {
    this.cleanup();
  },
};

Hooks.AnimatedNumber = {
  mounted() {
    this.currentValue = parseFloat(this.el.dataset.value);
    this.duration = parseFloat(this.el.dataset.duration);
    this.round = this.el.dataset.round === "true";
    this.isCurrency = this.el.dataset.currency === "true";
    this.el.innerHTML = this.formatNumber(this.currentValue);
  },

  updated() {
    const newValue = parseFloat(this.el.dataset.value);
    this.duration = parseFloat(this.el.dataset.duration);
    this.round = this.el.dataset.round === "true";
    this.animate(this.currentValue, newValue, this.round, this.duration);
  },

  animate(start, end, round, duration) {
    let startTimestamp = null;

    const ease = (x) => Math.sin((x * Math.PI) / 2);
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;

      let progress = Math.min((timestamp - startTimestamp) / duration, 1);
      progress = ease(progress);

      const newValue = progress * (end - start) + start;

      if (round) {
        this.currentValue = Math.floor(newValue);
      } else {
        this.currentValue = newValue;
      }

      this.el.innerHTML = this.formatNumber(this.currentValue);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  },

  formatNumber(value) {
    if (this.isCurrency) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    }

    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: this.round ? 0 : 2,
      trailingZeroDisplay: this.round ? "stripIfInteger" : "auto",
    }).format(value);
  },
};

Hooks.InfiniteScrollTrigger = {
  mounted() {
    this.el.addEventListener("scroll", (e) => {
      clearTimeout(this.loadMoreTimer);
      const event = this.el.dataset.scrollEvent;
      const form = this.el.closest("form");
      const eventParams = serialize(form);

      this.loadMoreTimer = setTimeout(() => {
        const resultsList = e.target;
        const hasMoreResults = "hasMoreResults" in this.el.dataset;
        const loadingMoreResults = "loadingMoreResults" in this.el.dataset;
        const userScrolledNearBottom =
          resultsList.scrollTop + resultsList.clientHeight + 150 >
          resultsList.scrollHeight;

        if (hasMoreResults && !loadingMoreResults && userScrolledNearBottom) {
          this.pushEvent(event, eventParams);
        }
      }, 250);
    });
  },
};

Hooks.ShowPasswordToggle = {
  mounted() {
    const input = this.el;
    const buttonElement = document.createElement("button");
    const buttonIcon = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0">
        </path>
        <circle cx="12" cy="12" r="3"></circle>
      </svg>
    `;

    const buttonClass = `
      tw:text-muted-foreground/80
      tw:absolute
      tw:inset-y-0
      tw:end-0
      tw:flex
      tw:h-full
      tw:w-9
      tw:items-center
      tw:justify-center
      tw:rounded-e-lg
      tw:outline-offset-2
      tw:transition-all
      tw:hover:scale-110
      tw:hover:text-zinc-500
      tw:focus:z-10
      tw:focus-visible:outline
      tw:focus-visible:outline-2
      tw:focus-visible:outline-zinc-500/70
      tw:disabled:pointer-events-none
      tw:disabled:cursor-not-allowed
      tw:disabled:opacity-50
    `;

    buttonElement.classList.value = buttonClass;
    buttonElement.innerHTML = buttonIcon;
    buttonElement.ariaLabel = "Toggle password visibility";
    buttonElement.setAttribute("aria-controls", input.id);
    buttonElement.type = "button";

    buttonElement.addEventListener("click", () => {
      input.type = input.type === "password" ? "text" : "password";
    });

    input.parentNode.insertBefore(buttonElement, input.nextSibling);
  },
};

export default {
  ...Hooks,
  ...alerts,
  ...calendar,
  ...colorpicker,
  ...cropper,
  ...devtools,
  ...formFields,
  ...groupActivityTracker,
  ...groupDues,
  ...groupEmailer,
  ...groupRecruitment,
  ...groupStatusUpdater,
  ...groupWebsiteEditorPageReorder,
  ...groupWebsiteEditorWysiwygEditor,
  ...imagepicker,
  ...publicPayments,
  ...sparkline,
  ...tagsEditor,
  ...telephone,
  ...websiteTemplateDesignerHtmlEditor,
};
